import type { InventoryItem, Money, Sale } from "./types";

export type PurchaseCostStatus = "known" | "unknown";

/** Legacy zero values were placeholders. Only an explicit known zero means free stock. */
export function purchaseCostIsKnown(record: Pick<InventoryItem | Sale, "purchaseCost" | "purchaseCostStatus">): boolean {
  return record.purchaseCostStatus === "known" || (record.purchaseCostStatus !== "unknown" && record.purchaseCost.amountMinor > 0);
}

export function saleProfitIsProvisional(sale: Sale): boolean {
  return !purchaseCostIsKnown(sale) || sale.costsStatus === "incomplete" || sale.marketplaceFeesStatus === "unknown";
}

export function resolvePurchaseCost(amount: unknown, status?: unknown): { amountMinor: number; status: PurchaseCostStatus } {
  if (!Number.isSafeInteger(amount) || (amount as number) < 0) throw new Error("Purchase cost must be zero or a positive amount, with at most two decimal places.");
  if (status !== undefined && status !== "known" && status !== "unknown") throw new Error("Choose whether the purchase cost is known.");
  if (status === "unknown" && amount !== 0) throw new Error("An unknown purchase cost cannot include an amount.");
  return { amountMinor: amount as number, status: status === "unknown" ? "unknown" : "known" };
}

export function parseCatalogueCost(value: string): number {
  const text = value.trim();
  if (!/^\d+(?:\.\d{1,2})?$/.test(text)) throw new Error("Enter a cost of 0 or more, with at most two decimal places.");
  const [whole, fraction = ""] = text.split(".");
  const amount = Number(whole) * 100 + Number(fraction.padEnd(2, "0"));
  return resolvePurchaseCost(amount).amountMinor;
}

export function validateAcquiredDate(value: unknown, now = new Date()): string | undefined {
  if (value === null) return undefined;
  if (typeof value !== "string" || !/^\d{4}-\d{2}-\d{2}$/.test(value)) throw new Error("Use YYYY-MM-DD for the acquisition date.");
  const date = new Date(`${value}T00:00:00.000Z`);
  if (!Number.isFinite(date.getTime()) || date.toISOString().slice(0, 10) !== value) throw new Error("Enter a valid acquisition date.");
  if (value > now.toISOString().slice(0, 10)) throw new Error("Acquisition date cannot be in the future.");
  return date.toISOString();
}

export function inventoryAge(item: Pick<InventoryItem, "acquiredAt" | "listedAt">, now = new Date()) {
  for (const [basis, value] of [["acquired", item.acquiredAt], ["listed", item.listedAt]] as const) {
    if (!value || !Number.isFinite(Date.parse(value))) continue;
    return { basis, date: value, days: Math.max(0, Math.floor((now.getTime() - Date.parse(value)) / 86_400_000)) };
  }
  return undefined;
}

export function inventoryAgeDays(item: Pick<InventoryItem, "acquiredAt" | "listedAt">, now = new Date()): number | undefined {
  return inventoryAge(item, now)?.days;
}

export function inventoryAgeLabel(item: Pick<InventoryItem, "acquiredAt" | "listedAt">, now = new Date()): string {
  const age = inventoryAge(item, now);
  if (!age) return "Stock age unknown";
  const days = `${age.days} ${age.days === 1 ? "day" : "days"}`;
  return age.basis === "listed" ? `At least ${days} · first listed` : `${days} in stock`;
}

export function catalogueMissingDetails(item: InventoryItem) {
  return { cost: !purchaseCostIsKnown(item), location: !item.stockLocation?.trim(), date: !item.acquiredAt || !Number.isFinite(Date.parse(item.acquiredAt)) };
}

export function catalogueNeedsDetails(item: InventoryItem) {
  return Object.values(catalogueMissingDetails(item)).some(Boolean);
}

export function purchaseCostInput(record: { purchaseCost: Money; purchaseCostStatus?: PurchaseCostStatus }) {
  return purchaseCostIsKnown(record) ? (record.purchaseCost.amountMinor / 100).toFixed(2) : "";
}
